import React, { memo } from "react";
import { createPortal } from "react-dom";

import GridColumn, { GridValueType } from "./GridColumn";
import GridCell from "./GridCell";
import PageSpecification from "../../types/system/PageSpecification";
import Utilities from "../../foundation/Utility";

interface GridBodyGroupRowProps<T> {
    columns: GridColumn<T>[];
    record: T;

    draggable?: boolean;
    dragContainerRef?: React.RefObject<HTMLTableSectionElement>;
    globalSelect: boolean;
    index: number;
    pageSpecification: PageSpecification;
    selected: boolean;
    selectedColumn: GridColumn<T>;
    singleSelected: boolean;

    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    onFocus?: (record: T, column: GridColumn<T>) => void;
    onBlur?: (record: T, column: GridColumn<T>) => void;
    onClick?: (record: T, column: GridColumn<T>) => void;
    onDoubleClick?: (record: T, column: GridColumn<T>) => void;
    prevRecordRequested?: () => void;
    nextRecordRequested?: () => void;
    prevColumnRequested?: () => void;
    nextColumnRequested?: () => void;
}

interface GridBodyGroupRowState {
    ref: React.RefObject<HTMLTableRowElement>;
}

class GridBodyGroupRow<T> extends React.Component<
    GridBodyGroupRowProps<T>,
    GridBodyGroupRowState
> {
    state: GridBodyGroupRowState = {
        ref: React.createRef<HTMLTableRowElement>(),
    };

    componentDidMount(): void {}

    cellSelected(record: T, column: GridColumn<T>) {
        return (
            this.props.selected &&
            (this.props.selectedColumn?.field ?? "") === column.field
        );
    }

    cellSingleSelected(record: T, column: GridColumn<T>) {
        return (
            this.props.singleSelected &&
            (this.props.selectedColumn?.field ?? "") === column.field
        );
    }

    getFieldValue(
        index: number,
        record: T,
        field: string,
        value?: GridValueType,
    ): any {
        if (!!field)
            switch (field) {
                default:
                    return Utilities.getPathToValue(field, record);
            }
        if (!!value)
            switch (value) {
                case "[RowNo]":
                    return String(index);
            }
        return null;
    }

    render(): React.ReactNode {
        const row = (
            <tr
                ref={this.state.ref}
                className={`group-row ${this.props.selected ? "selected" : ""}`}>
                {this.props.columns.map((column, index) => {
                    return (
                        <GridCell
                            key={index}
                            column={column}
                            record={this.props.record}
                            value={this.getFieldValue(
                                this.props.index + 1,
                                this.props.record,
                                column.field,
                                column.value,
                            )}
                            selected={this.cellSelected(
                                this.props.record,
                                column,
                            )}
                            globalSelect={this.props.globalSelect}
                            singleSelected={this.cellSingleSelected(
                                this.props.record,
                                column,
                            )}
                            icon={column.icon}
                            onKeyDown={this.props.onKeyDown}
                            onKeyUp={this.props.onKeyUp}
                            onFocus={this.props.onFocus}
                            onBlur={this.props.onBlur}
                            onClick={this.props.onClick}
                            onDoubleClick={this.props.onDoubleClick}
                            prevRecordRequested={this.props.prevRecordRequested}
                            nextRecordRequested={this.props.nextRecordRequested}
                            prevColumnRequested={this.props.prevColumnRequested}
                            nextColumnRequested={this.props.nextColumnRequested}
                        />
                    );
                })}
            </tr>
        );
        return this.props.dragContainerRef?.current && this.props.selected
            ? createPortal(row, this.props.dragContainerRef.current)
            : row;
    }
}

export default memo(GridBodyGroupRow);
